// @ts-check

/**
 * @typedef {"pointer" | "autopilot"} MotionSource
 * @typedef {"drift" | "zoom" | "warp"} MotionMode
 * @typedef {object} StarfieldOptions
 * @property {boolean} enabled
 * @property {number} seed
 * @property {number} density
 * @property {MotionSource} motionSource
 * @property {MotionMode} motionMode
 * @property {number} driftSpeed
 * @property {number} driftAngle
 * @property {number} zoomSpeed
 * @property {number} warpSpeed
 * @property {number} pointerInfluence
 * @property {number} pointerSmoothing
 * @property {number} twinkle
 * @property {number} layerCount
 * @property {number} streakStrength
 * @property {number} maxStarSize
 *
 * @typedef {object} LayerConfig
 * @property {number} countMultiplier
 * @property {number} parallax
 * @property {number} driftMultiplier
 * @property {number} zoomMultiplier
 * @property {number} radiusMultiplier
 * @property {number} sizeMin
 * @property {number} sizeMax
 * @property {number} opacityMin
 * @property {number} opacityMax
 * @property {number} twinkleMultiplier
 * @property {number} streakMultiplier
 * @property {number} glowMultiplier
 *
 * @typedef {object} Star
 * @property {number} u
 * @property {number} v
 * @property {number} dirX
 * @property {number} dirY
 * @property {number} phase
 * @property {number} twinkleFreq
 * @property {number} twinklePhase
 * @property {number} size
 * @property {number} baseOpacity
 * @property {number} hue
 * @property {number} sat
 * @property {number} lit
 *
 * @typedef {object} LayerState
 * @property {LayerConfig} config
 * @property {Star[]} stars
 *
 * @typedef {{x: number, y: number}} Point
 * @typedef {{x: number, y: number, vx: number, vy: number}} Drift
 */

/** @type {StarfieldOptions} */
const DEFAULTS = {
  enabled: true,
  seed: 1,
  density: 1,
  motionSource: "pointer",
  motionMode: "zoom",
  driftSpeed: 0.35,
  driftAngle: -25,
  zoomSpeed: 0.18,
  warpSpeed: 0.45,
  pointerInfluence: 0.45,
  pointerSmoothing: 0.08,
  twinkle: 0.35,
  layerCount: 3,
  streakStrength: 0.18,
  maxStarSize: 2.4,
};
/** @type {LayerConfig[]} */
const LAYER_PRESETS = [
  {
    countMultiplier: 1.3,
    parallax: 0.35,
    driftMultiplier: 0.45,
    zoomMultiplier: 0.75,
    radiusMultiplier: 0.95,
    sizeMin: 0.45,
    sizeMax: 1.15,
    opacityMin: 0.1,
    opacityMax: 0.45,
    twinkleMultiplier: 0.55,
    streakMultiplier: 0,
    glowMultiplier: 0,
  },
  {
    countMultiplier: 0.7,
    parallax: 0.75,
    driftMultiplier: 0.8,
    zoomMultiplier: 1.1,
    radiusMultiplier: 1.15,
    sizeMin: 0.65,
    sizeMax: 1.6,
    opacityMin: 0.16,
    opacityMax: 0.7,
    twinkleMultiplier: 0.8,
    streakMultiplier: 0.2,
    glowMultiplier: 0.25,
  },
  {
    countMultiplier: 0.36,
    parallax: 1.15,
    driftMultiplier: 1.15,
    zoomMultiplier: 1.45,
    radiusMultiplier: 1.35,
    sizeMin: 0.95,
    sizeMax: 2.4,
    opacityMin: 0.25,
    opacityMax: 0.95,
    twinkleMultiplier: 1,
    streakMultiplier: 1,
    glowMultiplier: 1,
  },
];
/** @param {number} n @param {number} min @param {number} max */
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}
/** @param {number} n */
function wrap01(n) {
  return ((n % 1) + 1) % 1;
}
/** @param {number} n */
function frac(n) {
  return n - Math.floor(n);
}
/** @param {number} seed */
function mulberry32(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
/** @param {number} n */
function round3(n) {
  return Number(n.toFixed(3));
}
/**
 * @param {Partial<StarfieldOptions> | undefined} patch
 * @param {StarfieldOptions} [base]
 * @returns {StarfieldOptions}
 */
function normalizeOptions(patch, base = DEFAULTS) {
  const next = { ...base, ...(patch || {}) };
  return {
    enabled: Boolean(next.enabled),
    seed: Math.max(1, Math.floor(next.seed || 1)),
    density: clamp(next.density, 0.2, 2.5),
    motionSource: next.motionSource === "autopilot" ? "autopilot" : "pointer",
    motionMode: next.motionMode === "drift" || next.motionMode === "warp" ? next.motionMode : "zoom",
    driftSpeed: clamp(next.driftSpeed, 0, 3),
    driftAngle: ((next.driftAngle % 360) + 360) % 360,
    zoomSpeed: clamp(next.zoomSpeed, 0, 3),
    warpSpeed: clamp(next.warpSpeed, 0, 3),
    pointerInfluence: clamp(next.pointerInfluence, 0, 1),
    pointerSmoothing: clamp(next.pointerSmoothing, 0.01, 0.3),
    twinkle: clamp(next.twinkle, 0, 1),
    layerCount: clamp(Math.round(next.layerCount), 1, LAYER_PRESETS.length),
    streakStrength: clamp(next.streakStrength, 0, 1),
    maxStarSize: clamp(next.maxStarSize, 0.6, 4),
  };
}
export default class Starfield {
  /** @type {HTMLCanvasElement} */
  canvas;
  /** @type {CanvasRenderingContext2D} */
  ctx;
  /** @type {StarfieldOptions} */
  options;
  /** @type {LayerState[]} */
  layers = [];
  /** @type {number | null} */
  rafId = null;
  dpr = 1;
  W = 0;
  H = 0;
  minDim = 0;
  lastTime = 0;
  epochOffset = 0;
  pointerTarget = { x: 0, y: 0 };
  liveTarget = { x: 0, y: 0 };
  /**
   * @param {HTMLCanvasElement} canvas
   * @param {Partial<StarfieldOptions>} [options]
   */
  constructor(canvas, options = {}) {
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) throw new Error("Canvas 2D context unavailable");
    this.canvas = canvas;
    this.ctx = ctx;
    this.options = normalizeOptions(options);
    this.epochOffset = this.seedEpochOffset();
    this.rebuild();
    this.resize();
    this.liveTarget = this.computeRawSourceTarget(this.currentStableTime());
  }
  start() {
    if (this.rafId !== null) return;
    this.lastTime = 0;
    /** @param {number} ms */
    const loop = ms => {
      this.rafId = requestAnimationFrame(loop);
      this.render(ms * 0.001);
    };
    this.rafId = requestAnimationFrame(loop);
  }
  stop() {
    if (this.rafId === null) return;
    cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }
  destroy() {
    this.stop();
    this.ctx.clearRect(0, 0, this.W, this.H);
  }
  resize() {
    const dpr = window.devicePixelRatio || 1;
    const W = this.canvas.clientWidth || this.canvas.width || window.innerWidth;
    const H = this.canvas.clientHeight || this.canvas.height || window.innerHeight;
    this.dpr = dpr;
    this.W = W;
    this.H = H;
    this.minDim = Math.min(W, H);
    this.canvas.width = Math.max(1, Math.round(W * dpr));
    this.canvas.height = Math.max(1, Math.round(H * dpr));
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  /** @param {Partial<StarfieldOptions>} patch */
  setOptions(patch) {
    const prev = this.options;
    this.options = normalizeOptions(patch, prev);
    if (
      this.options.seed !== prev.seed ||
      this.options.density !== prev.density ||
      this.options.layerCount !== prev.layerCount ||
      this.options.maxStarSize !== prev.maxStarSize
    ) {
      this.epochOffset = this.seedEpochOffset();
      this.rebuild();
      if (this.options.motionSource === "autopilot") {
        this.liveTarget = this.computeRawSourceTarget(this.currentStableTime());
      }
    } else if (this.options.motionSource === "autopilot" && prev.motionSource !== this.options.motionSource) {
      this.liveTarget = this.computeRawSourceTarget(this.currentStableTime());
    }
    if (!this.options.enabled) {
      this.ctx.clearRect(0, 0, this.W, this.H);
    }
  }
  getOptions() {
    return { ...this.options };
  }
  /**
   * @param {number} x
   * @param {number} y
   * @param {{ immediate?: boolean }} [options]
   */
  setPointer(x, y, options = {}) {
    this.pointerTarget.x = clamp(x, -1, 1);
    this.pointerTarget.y = clamp(y, -1, 1);
    if (options.immediate) {
      this.liveTarget = this.computeRawSourceTarget(this.currentStableTime());
    }
  }
  /** @param {{ immediate?: boolean }} [options] */
  clearPointer(options = {}) {
    this.pointerTarget.x = 0;
    this.pointerTarget.y = 0;
    if (options.immediate) {
      this.liveTarget = this.computeRawSourceTarget(this.currentStableTime());
    }
  }
  /** @param {number} [seed] */
  reseed(seed = Math.floor(Math.random() * 1_000_000_000)) {
    this.options = normalizeOptions({ seed }, this.options);
    this.epochOffset = this.seedEpochOffset();
    this.rebuild();
    if (this.options.motionSource === "autopilot") {
      this.liveTarget = this.computeRawSourceTarget(this.currentStableTime());
    }
    return this.options.seed;
  }
  rebuild() {
    /** @type {LayerState[]} */
    const layers = [];
    for (let i = 0; i < this.options.layerCount; i++) {
      const config = LAYER_PRESETS[i];
      layers.push({
        config,
        stars: this.buildLayerStars(i, config),
      });
    }
    this.layers = layers;
  }
  /** @param {number} index @param {LayerConfig} config */
  buildLayerStars(index, config) {
    const rng = mulberry32(this.options.seed + index * 9973);
    const count = Math.max(24, Math.round(170 * this.options.density * config.countMultiplier));
    const maxStarSize = Math.max(config.sizeMin, Math.min(config.sizeMax, this.options.maxStarSize));
    /** @type {Star[]} */
    const stars = [];
    for (let i = 0; i < count; i++) {
      let dirX = rng() * 2 - 1;
      let dirY = rng() * 2 - 1;
      const mag = Math.hypot(dirX, dirY) || 1;
      dirX /= mag;
      dirY /= mag;
      const size = config.sizeMin + Math.pow(rng(), 2.15) * (maxStarSize - config.sizeMin);
      stars.push({
        u: rng(),
        v: rng(),
        dirX,
        dirY,
        phase: rng(),
        twinkleFreq: 0.25 + rng() * 1.35,
        twinklePhase: rng() * Math.PI * 2,
        size,
        baseOpacity: config.opacityMin + rng() * (config.opacityMax - config.opacityMin),
        hue: 190 + rng() * 70,
        sat: 4 + rng() * 22,
        lit: 78 + rng() * 19,
      });
    }
    return stars;
  }
  seedEpochOffset() {
    return (this.options.seed % 100_000) * 0.1;
  }
  currentStableTime() {
    return Date.now() * 0.001 + this.epochOffset;
  }
  /** @param {number} t */
  render(t) {
    if (!this.options.enabled) {
      this.ctx.clearRect(0, 0, this.W, this.H);
      this.lastTime = t;
      return;
    }
    const dt = this.lastTime ? Math.min(0.05, Math.max(0.001, t - this.lastTime)) : 0.016;
    this.lastTime = t;
    const stableT = this.currentStableTime();
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.W, this.H);
    const sourceTarget = this.computeSourceTarget(stableT, dt);
    const drift = this.computeDrift(stableT);
    const centerBase = this.computeCenterBase(stableT);
    for (const layer of this.layers) {
      this.renderLayer(layer, stableT, dt, sourceTarget, drift, centerBase);
    }
  }
  /** @param {number} t @returns {Point} */
  computeRawSourceTarget(t) {
    if (this.options.motionSource === "autopilot") {
      const seedA = (this.options.seed % 997) * 0.013;
      const seedB = (this.options.seed % 463) * 0.021;
      return {
        x: 0.52 * Math.sin(t * 0.19 + seedA) + 0.18 * Math.sin(t * 0.43 + seedB * 0.7),
        y: 0.46 * Math.cos(t * 0.17 + seedB) + 0.15 * Math.sin(t * 0.37 + seedA * 0.6),
      };
    }
    return {
      x: this.pointerTarget.x * this.options.pointerInfluence,
      y: this.pointerTarget.y * this.options.pointerInfluence,
    };
  }
  /** @param {number} t @param {number} dt @returns {Point} */
  computeSourceTarget(t, dt) {
    const target = this.computeRawSourceTarget(t);
    const smoothing = 1 - Math.pow(1 - this.options.pointerSmoothing, 60 * dt);
    this.liveTarget.x += (target.x - this.liveTarget.x) * smoothing;
    this.liveTarget.y += (target.y - this.liveTarget.y) * smoothing;
    return this.liveTarget;
  }
  /** @param {number} t @returns {Drift} */
  computeDrift(t) {
    const angle = (this.options.driftAngle * Math.PI) / 180;
    const travel = t * this.options.driftSpeed * 0.018;
    const seedA = (this.options.seed % 431) * 0.031;
    const seedB = (this.options.seed % 797) * 0.017;
    const swayX = 0.06 * Math.sin(t * 0.11 + seedA) + 0.025 * Math.sin(t * 0.29 + seedB);
    const swayY = 0.06 * Math.cos(t * 0.09 + seedB) + 0.022 * Math.sin(t * 0.24 + seedA);
    return {
      x: Math.cos(angle) * travel + swayX,
      y: Math.sin(angle) * travel + swayY,
      vx: Math.cos(angle) * this.options.driftSpeed * 0.018,
      vy: Math.sin(angle) * this.options.driftSpeed * 0.018,
    };
  }
  /** @param {number} t @returns {Point} */
  computeCenterBase(t) {
    const seedA = (this.options.seed % 577) * 0.023;
    const seedB = (this.options.seed % 313) * 0.019;
    return {
      x: 0.12 * Math.sin(t * 0.09 + seedA) + 0.04 * Math.sin(t * 0.27 + seedB),
      y: 0.1 * Math.cos(t * 0.08 + seedB) + 0.035 * Math.sin(t * 0.22 + seedA),
    };
  }
  /**
   * @param {LayerState} layer
   * @param {number} t
   * @param {number} dt
   * @param {Point} sourceTarget
   * @param {Drift} drift
   * @param {Point} centerBase
   */
  renderLayer(layer, t, dt, sourceTarget, drift, centerBase) {
    const ctx = this.ctx;
    const { config } = layer;
    const twinkleScale = this.options.twinkle * 0.35 * config.twinkleMultiplier;
    if (this.options.motionMode === "drift") {
      const cameraX = drift.x * config.driftMultiplier + sourceTarget.x * 0.11 * config.parallax;
      const cameraY = drift.y * config.driftMultiplier + sourceTarget.y * 0.11 * config.parallax;
      const streakLen = this.options.streakStrength * config.streakMultiplier * (8 + 20 * config.parallax);
      const motionAngle = Math.atan2(drift.vy, drift.vx);
      for (const star of layer.stars) {
        const x = wrap01(star.u + cameraX) * this.W;
        const y = wrap01(star.v + cameraY) * this.H;
        const opacity = clamp(
          star.baseOpacity + twinkleScale * Math.sin(t * star.twinkleFreq + star.twinklePhase),
          0.04,
          1,
        );
        ctx.save();
        ctx.globalAlpha = 1;
        ctx.strokeStyle = `hsla(${Math.round(star.hue)}, ${Math.round(star.sat)}%, ${Math.round(star.lit)}%, ${opacity})`;
        if (streakLen > 0.5) {
          ctx.lineWidth = Math.max(0.8, star.size * 0.85);
          ctx.beginPath();
          ctx.moveTo(x - Math.cos(motionAngle) * streakLen, y - Math.sin(motionAngle) * streakLen);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
        if (config.glowMultiplier > 0 && star.size > 1.2) {
          ctx.shadowColor = `hsl(${Math.round(star.hue)} ${Math.round(star.sat + 18)}% ${Math.round(star.lit)}%)`;
          ctx.shadowBlur = star.size * 3.8 * config.glowMultiplier;
        }
        ctx.fillStyle = `hsla(${Math.round(star.hue)}, ${Math.round(star.sat)}%, ${Math.round(star.lit)}%, ${opacity})`;
        ctx.beginPath();
        ctx.arc(x, y, star.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      return;
    }
    const sourceCenterX = this.W * 0.5 + sourceTarget.x * this.W * 0.5;
    const sourceCenterY = this.H * 0.5 + sourceTarget.y * this.H * 0.5;
    if (this.options.motionMode === "warp") {
      const centerX =
        this.options.motionSource === "pointer"
          ? sourceCenterX
          : this.W * 0.5 + (centerBase.x * 0.35 + sourceTarget.x * 0.65) * this.W * 0.35;
      const centerY =
        this.options.motionSource === "pointer"
          ? sourceCenterY
          : this.H * 0.5 + (centerBase.y * 0.35 + sourceTarget.y * 0.65) * this.H * 0.35;
      const speed = this.options.warpSpeed * 0.22 * config.zoomMultiplier;
      const edgePad = 0.14 * config.radiusMultiplier;
      const startRadius = 0.018;
      const endRadius = 1 + edgePad;
      const streakUnit = 0.035 * this.options.streakStrength * config.streakMultiplier;
      for (const star of layer.stars) {
        const progress = frac(t * speed + star.phase);
        const eased = progress * progress;
        const vx = star.dirX;
        const vy = star.dirY;
        const radius = startRadius + eased * (endRadius - startRadius);
        const nx = vx * radius;
        const ny = vy * radius;
        const x = centerX + nx * this.W;
        const y = centerY + ny * this.H;
        if (x < -120 || x > this.W + 120 || y < -120 || y > this.H + 120) continue;
        const opacity = clamp(
          star.baseOpacity * (0.15 + progress * 1.15) +
            twinkleScale * Math.sin(t * star.twinkleFreq + star.twinklePhase),
          0.04,
          1,
        );
        const tailRadius = Math.max(startRadius, radius - streakUnit * (0.4 + progress * 1.3));
        const tailX = centerX + vx * tailRadius * this.W;
        const tailY = centerY + vy * tailRadius * this.H;
        ctx.save();
        ctx.globalAlpha = 1;
        if (streakUnit > 0.0001) {
          ctx.strokeStyle = `hsla(${Math.round(star.hue)}, ${Math.round(star.sat)}%, ${Math.round(star.lit)}%, ${opacity})`;
          ctx.lineWidth = Math.max(0.8, star.size * 0.9);
          ctx.beginPath();
          ctx.moveTo(tailX, tailY);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
        if (config.glowMultiplier > 0 && star.size > 1.15) {
          ctx.shadowColor = `hsl(${Math.round(star.hue)} ${Math.round(star.sat + 18)}% ${Math.round(star.lit)}%)`;
          ctx.shadowBlur = star.size * 3.5 * config.glowMultiplier;
        }
        ctx.fillStyle = `hsla(${Math.round(star.hue)}, ${Math.round(star.sat)}%, ${Math.round(star.lit)}%, ${opacity})`;
        ctx.beginPath();
        ctx.arc(x, y, star.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      return;
    }
    const centerX =
      this.options.motionSource === "pointer"
        ? sourceCenterX
        : this.W * 0.5 + (centerBase.x * 0.3 + sourceTarget.x * 0.65) * this.W * 0.22;
    const centerY =
      this.options.motionSource === "pointer"
        ? sourceCenterY
        : this.H * 0.5 + (centerBase.y * 0.3 + sourceTarget.y * 0.65) * this.H * 0.22;
    const zoomRate = Math.max(0.001, this.options.zoomSpeed) * 0.16 * config.zoomMultiplier;
    const maxRadius = this.minDim * 0.85 * config.radiusMultiplier;
    for (const star of layer.stars) {
      const progress = frac(t * zoomRate + star.phase);
      const eased = progress * progress;
      const radius = (0.02 + eased * 1.2) * maxRadius;
      const x = centerX + star.dirX * radius;
      const y = centerY + star.dirY * radius * (0.92 + config.parallax * 0.08);
      const opacity = clamp(
        star.baseOpacity * (0.45 + progress * 0.8) + twinkleScale * Math.sin(t * star.twinkleFreq + star.twinklePhase),
        0.04,
        1,
      );
      const streakLen = this.options.streakStrength * config.streakMultiplier * (6 + 28 * progress);
      ctx.save();
      ctx.globalAlpha = 1;
      if (streakLen > 0.35) {
        ctx.strokeStyle = `hsla(${Math.round(star.hue)}, ${Math.round(star.sat)}%, ${Math.round(star.lit)}%, ${opacity})`;
        ctx.lineWidth = Math.max(0.8, star.size * 0.8);
        ctx.beginPath();
        ctx.moveTo(x - star.dirX * streakLen, y - star.dirY * streakLen);
        ctx.lineTo(x, y);
        ctx.stroke();
      }
      if (config.glowMultiplier > 0 && star.size > 1.15) {
        ctx.shadowColor = `hsl(${Math.round(star.hue)} ${Math.round(star.sat + 18)}% ${Math.round(star.lit)}%)`;
        ctx.shadowBlur = star.size * 3.5 * config.glowMultiplier;
      }
      ctx.fillStyle = `hsla(${Math.round(star.hue)}, ${Math.round(star.sat)}%, ${Math.round(star.lit)}%, ${opacity})`;
      ctx.beginPath();
      ctx.arc(x, y, star.size, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
}
/** @returns {StarfieldOptions} */
export function starfieldDefaults() {
  return {
    ...DEFAULTS,
    density: round3(DEFAULTS.density),
    driftSpeed: round3(DEFAULTS.driftSpeed),
    driftAngle: round3(DEFAULTS.driftAngle),
    zoomSpeed: round3(DEFAULTS.zoomSpeed),
    warpSpeed: round3(DEFAULTS.warpSpeed),
    pointerInfluence: round3(DEFAULTS.pointerInfluence),
    pointerSmoothing: round3(DEFAULTS.pointerSmoothing),
    twinkle: round3(DEFAULTS.twinkle),
    streakStrength: round3(DEFAULTS.streakStrength),
    maxStarSize: round3(DEFAULTS.maxStarSize),
  };
}

/**
 * @param {HTMLCanvasElement} canvas
 * @param {Partial<StarfieldOptions>} [options]
 */
export function mountStarfield(canvas, options = {}) {
  const starfield = new Starfield(canvas, options);
  const pointerStorageKey = "gg-starfield-pointer-v1";
  const pointerStorageMaxAgeMs = 3000;
  /** @type {{ x: number, y: number } | null} */
  let lastPointer = null;
  let pointerSaveQueued = false;
  let resizeRafId = 0;

  const savePointer = () => {
    pointerSaveQueued = false;
    if (!lastPointer) return;
    try {
      sessionStorage.setItem(pointerStorageKey, JSON.stringify({ ...lastPointer, updatedAt: Date.now() }));
    } catch (_err) {}
  };

  const queuePointerSave = () => {
    if (pointerSaveQueued) return;
    pointerSaveQueued = true;
    requestAnimationFrame(savePointer);
  };

  const restorePointer = () => {
    try {
      const raw = sessionStorage.getItem(pointerStorageKey);
      const saved = raw ? JSON.parse(raw) : null;
      if (
        saved &&
        Number.isFinite(saved.x) &&
        Number.isFinite(saved.y) &&
        Number.isFinite(saved.updatedAt) &&
        Date.now() - saved.updatedAt <= pointerStorageMaxAgeMs
      ) {
        starfield.setPointer(saved.x, saved.y, { immediate: true });
      }
    } catch (_err) {}
  };

  /** @param {PointerEvent} evt */
  const updatePointer = evt => {
    const x = (evt.clientX / window.innerWidth) * 2 - 1;
    const y = (evt.clientY / window.innerHeight) * 2 - 1;
    lastPointer = { x, y };
    starfield.setPointer(x, y);
    queuePointerSave();
  };

  /** @param {PointerEvent} evt */
  const handlePointerDown = evt => {
    updatePointer(evt);
    savePointer();
  };

  const clearPointerImmediately = () => {
    savePointer();
    starfield.clearPointer({ immediate: true });
  };
  const clearPointer = () => starfield.clearPointer();

  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      clearPointerImmediately();
    }
  };

  if (starfield.options.motionSource === "pointer") {
    restorePointer();
    window.addEventListener("pointermove", updatePointer, { passive: true });
    window.addEventListener("pointerdown", handlePointerDown, { passive: true });
    window.addEventListener("pointerleave", clearPointer);
    window.addEventListener("blur", clearPointerImmediately);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", savePointer);
  }

  const resizeStarfield = () => starfield.resize();
  window.addEventListener("resize", resizeStarfield, { passive: true });
  resizeRafId = requestAnimationFrame(resizeStarfield);

  /** @type {ResizeObserver | null} */
  const resizeObserver = "ResizeObserver" in window ? new ResizeObserver(resizeStarfield) : null;
  resizeObserver?.observe(canvas);

  starfield.start();

  return {
    starfield,
    destroy() {
      if (resizeRafId) cancelAnimationFrame(resizeRafId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", resizeStarfield);
      if (starfield.options.motionSource === "pointer") {
        window.removeEventListener("pointermove", updatePointer);
        window.removeEventListener("pointerdown", handlePointerDown);
        window.removeEventListener("pointerleave", clearPointer);
        window.removeEventListener("blur", clearPointerImmediately);
        window.removeEventListener("pagehide", savePointer);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      starfield.destroy();
    },
  };
}
